import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SignInBrandLogo } from '@l2u/web-shared';
import { ErrorMessage } from '@l2u/web-shared';
import { CenteredSpinner } from '@l2u/web-shared';
import { PoweredByFooter } from '@l2u/web-shared';
import { setAdminAccessToken } from '@l2u/web-shared';
import { createEphemeralAdminSupabase, getAdminSupabase } from '@l2u/web-shared';
import {
  SignInButton,
  SignInField,
  SignInHeading,
  SignInHelp,
  SignInLayout,
  SignInPasswordInput,
  SignInRule,
} from '@l2u/web-shared';
import { readCredentials } from '../lib/auth/redeemCredentials.ts';
import { OneTimeLinkSession, PasswordSetSignInFailed } from '../lib/auth/oneTimeLinkSession.ts';

const MIN_PASSWORD_LENGTH = 8;

type Phase = 'confirm' | 'redeeming' | 'set-password' | 'password-set';

/**
 * The page the recovery email lands on. Every state renders inside
 * SignInLayout, so the flow never flashes a different-coloured page.
 *
 * NOTHING is redeemed on load. The link is a one-time credential, and mail
 * security scanners that EXECUTE the page (Proofpoint / Mimecast URL sandboxes,
 * some Safe Links modes) used to redeem it for us: the real admin then saw
 * "this link didn't work", and the sandbox held a live admin session. So:
 *
 *  1. 'confirm' — the credential is only READ from the URL (fragment session,
 *     ?code or ?token_hash, whichever arrived) and held in state. The redeem
 *     waits for a click on Continue. That stops page-load redeems; it does NOT
 *     stop a sandbox that also clicks buttons (some detonation products do).
 *     What such a sandbox still cannot get is a PERSISTED session or a
 *     password change, and the real admin notices: their link reads as used,
 *     and they request another.
 *  2. The click redeems into a memory-only client (createEphemeralAdminSupabase)
 *     and strips the credential from the address bar. The persisted admin
 *     client — and setAdminAccessToken — see nothing yet.
 *  3. Only after updateUser({ password }) succeeds is the session handed to the
 *     admin client (completePasswordSet). Leaving before that revokes the
 *     in-memory session (abandonSession, local scope).
 *
 * The latch, the late-redeem revoke and the abandon rules live in
 * OneTimeLinkSession (lib/auth/oneTimeLinkSession.ts), shared with
 * AdminAcceptInvite.
 */
export function ResetPassword() {
  const navigate = useNavigate();
  // Read once, on the first render: step 2 strips the URL, and a re-read after
  // that would find nothing.
  const [creds] = useState(
    () => readCredentials(window.location.search, window.location.hash).creds,
  );
  const [phase, setPhase] = useState<Phase>('confirm');
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // A URL with nothing redeemable is refused up front — there is no credential
  // for a click to protect, and no reason to make the user press Continue to
  // learn the link is broken. redeemCredentials would throw this same error.
  const [error, setError] = useState<unknown>(() =>
    creds ? null : new Error('This link is missing the credentials needed to continue.'),
  );
  // Always 'recovery' here: this route only ever redeems a password-reset
  // credential, whatever `type` the link happens to carry.
  const [session] = useState(
    () => new OneTimeLinkSession(creds, 'recovery', createEphemeralAdminSupabase),
  );

  // Abandoning the page (a link away, closing the tab) with a redeemed but
  // unfinished session revokes it, so the live session never outlives the tab.
  useEffect(() => {
    session.enter();
    const abandon = () => session.leave();
    window.addEventListener('pagehide', abandon);
    return () => {
      window.removeEventListener('pagehide', abandon);
      abandon();
    };
  }, [session]);

  async function onContinue() {
    if (session.held) return;
    setError(null);
    setPhase('redeeming');
    try {
      const outcome = await session.redeem();
      if (outcome.status !== 'redeemed') return;
      // Only reached with a real session — redeemCredentials throws otherwise,
      // rather than letting the form open and blaming the new password for a
      // link that was already spent.
      navigate('/reset-password', { replace: true });
      setEmail(outcome.email);
      setPhase('set-password');
    } catch (err) {
      setError(err);
    }
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(new Error(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`));
      return;
    }
    if (password !== confirm) {
      setError(new Error('Passwords do not match.'));
      return;
    }
    if (!session.held) {
      setError(new Error('This reset link is no longer active. Request a new one.'));
      return;
    }
    setSubmitting(true);
    try {
      const token = await session.complete(getAdminSupabase().auth, password);
      setAdminAccessToken(token);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof PasswordSetSignInFailed) setPhase('password-set');
      else setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  /** The tenant's logo, or its name. Shared by every state. */
  const lockup = <SignInBrandLogo />;

  if (phase === 'confirm' || phase === 'redeeming') {
    if (error) {
      return (
        <SignInLayout audience="admin">
          {lockup}
          {/* The heading is now the PROBLEM. It used to be
              `branding.companyName ?? 'Compliance'` — the workspace's name, as
              an <h1>, with the actual message demoted to a paragraph below it,
              so the one line a screen reader announces first said nothing about
              what had gone wrong. The tenant's name is still on the page, in
              the lockup above. */}
          <SignInHeading eyebrow="Admin portal" title="This reset link didn’t work">
            It may have expired, or it may already have been used — reset links work once.
          </SignInHeading>

          <div role="alert" className="empty:hidden">
            <ErrorMessage error={error} />
          </div>

          <SignInRule />

          <SignInHelp>
            <div>
              <Link to="/forgot-password">Request a new reset link</Link>
            </div>
            <div>
              <Link to="/login">Back to sign in</Link>
            </div>
            <PoweredByFooter />
          </SignInHelp>
        </SignInLayout>
      );
    }
    if (phase === 'confirm') {
      return (
        <SignInLayout audience="admin">
          {lockup}
          <SignInHeading eyebrow="Admin portal" title="Reset your password">
            Continue to choose a new password for your account.
          </SignInHeading>

          <SignInButton audience="admin" type="button" onClick={() => void onContinue()}>
            Continue to reset your password
          </SignInButton>

          <SignInRule />

          <SignInHelp>
            <div>
              <Link to="/login">Back to sign in</Link>
            </div>
            <PoweredByFooter />
          </SignInHelp>
        </SignInLayout>
      );
    }
    return (
      <SignInLayout audience="admin">
        <div className="flex flex-col items-center gap-4 py-10">
          <CenteredSpinner />
          <p className="m-0" style={{ fontSize: 15, color: '#5B5950' }}>
            Verifying your reset link…
          </p>
        </div>
      </SignInLayout>
    );
  }

  if (phase === 'password-set') {
    return (
      <SignInLayout audience="admin">
        {lockup}
        <SignInHeading eyebrow="Admin portal" title="Password updated">
          Your password was changed, but we couldn’t sign you in automatically. Sign in with
          your new password.
        </SignInHeading>

        <SignInRule />

        <SignInHelp>
          <div>
            <Link to="/login">Go to sign in</Link>
          </div>
          <PoweredByFooter />
        </SignInHelp>
      </SignInLayout>
    );
  }

  return (
    <SignInLayout audience="admin">
      {lockup}

      <SignInHeading eyebrow="Admin portal" title="Reset your password">
        Choose a new password to finish{' '}
        {email ? (
          <>
            resetting <strong className="font-semibold text-[#14120D]">{email}</strong>
          </>
        ) : (
          'resetting your account'
        )}
        .
      </SignInHeading>

      <form className="flex flex-col gap-4" onSubmit={onSubmit}>
        <SignInField
          label="New password"
          audience="admin"
          hint={`At least ${MIN_PASSWORD_LENGTH} characters`}
        >
          <SignInPasswordInput
            audience="admin"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={MIN_PASSWORD_LENGTH}
          />
        </SignInField>

        <SignInField label="Confirm password" audience="admin">
          <SignInPasswordInput
            audience="admin"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={MIN_PASSWORD_LENGTH}
          />
        </SignInField>

        <div role="alert" className="empty:hidden">
          <ErrorMessage error={error} />
        </div>

        <SignInButton
          audience="admin"
          type="submit"
          loading={submitting}
          disabled={!password || !confirm}
        >
          Update password and sign in
        </SignInButton>
      </form>

      <SignInRule />

      <SignInHelp>
        <PoweredByFooter />
      </SignInHelp>
    </SignInLayout>
  );
}
